import { createRouter, createWebHistory } from "vue-router";
import Login from "@/views/Login.vue";
import HomeView from "@/views/HomeView.vue";
import BrandList from "@/views/brands/BrandList.vue";
import CategoryList from "@/views/categories/CategoryList.vue";
import MasterData from "@/views/master-data/MasterData.vue";
import CarList from "@/views/cars/CarList.vue";
import CarView from "@/views/cars/View.vue";
import CarCreate from "@/views/cars/Create.vue";
import CarEdit from "@/views/cars/Edit.vue";
import OtpList from "@/views/admin/OtpList.vue";
import ActivityLogsList from "@/views/admin/ActivityLogs/ActivityLogsList.vue";
import ModeratorsList from "@/views/admin/Moderators/ModeratorsList.vue";
import ModeratorDetail from "@/views/admin/Moderators/ModeratorDetail.vue";
import LeadsList from "@/views/admin/Leads/LeadsList.vue";

const routes = [
  { path: "/", redirect: "/dashboard" },

  // ─── Auth ───────────────────────────────────────────
  {
    path: "/login",
    name: "Login",
    component: Login,
    meta: { guestOnly: true },
  },

  // ─── Dashboard ──────────────────────────────────────
  {
    path: "/dashboard",
    name: "Dashboard",
    component: HomeView,
    meta: { requiresAuth: true },
  },
  {
    path: "/analytics",
    name: "Analytics",
    component: () => import("@/views/analytics/AnalyticsView.vue"),
    meta: { requiresAuth: true, requiresAnalytics: true },
  },

  {
    path: "/roles",
    name: "Roles",
    component: () => import("@/views/roles/RoleList.vue"),
    meta: { requiresAuth: true, requiresAdmin: true },
  },

  {
    path: "/users",
    name: "Users",
    component: () => import("@/views/users/UserList.vue"),
    meta: { requiresAuth: true, requiresAdmin: true },
  },

  {
    path: "/users/create",
    name: "UserCreate",
    component: () => import("@/views/users/UserCreate.vue"),
  },
  {
    path: "/users/edit/:id",
    name: "UserEdit",
    component: () => import("@/views/users/UserEdit.vue"),
    props: true,
    meta: { requiresAuth: true, requiresAdmin: true },
  },
  {
    path: "/owners",
    name: "Owners",
    component: () => import("@/views/owners/OwnerList.vue"),
    meta: { requiresAuth: true, requiresAdmin: true },
  },
  {
    path: "/owners/create/:userId",
    name: "OwnerCreate",
    component: () => import("@/views/owners/OwnerCreate.vue"),
    props: true,
    meta: { requiresAuth: true, requiresAdmin: true },
  },
  {
    path: "/owners/edit/:id",
    name: "OwnerEdit",
    component: () => import("@/views/owners/OwnerEdit.vue"),
    props: true,
    meta: { requiresAuth: true, requiresAdmin: true },
  },

  // ─── Admin only ─────────────────────────────────────

  {
    path: "/otps",
    name: "Otps",
    component: OtpList,
    meta: { requiresAuth: true, requiresAdmin: true },
  },
  {
    path: "/cars/:id/status-history",
    name: "CarStatusHistory",
    component: () => import("@/views/cars/CarStatusHistory.vue"),
    meta: { requiresAuth: true }, // adjust to match your existing route meta convention
  },

  {
    path: "/activityLogs",
    name: "ActivityLogs",
    component: ActivityLogsList,
    meta: { requiresAuth: true, requiresAdmin: true },
  },
  {
    path: "/moderators",
    name: "ModeratorsList",
    component: ModeratorsList,
    meta: { requiresAuth: true, requiresAdmin: true },
  },
  {
    path: "/moderators/:id",
    name: "ModeratorDetail",
    component: ModeratorDetail,
    props: true,
    meta: { requiresAuth: true, requiresAdmin: true },
  },
  {
    path: "/leads",
    name: "Leads",
    component: LeadsList,
    meta: { requiresAuth: true, requiresAdmin: true },
  },
  {
    path: "/brand",
    name: "Brand",
    component: BrandList,
    meta: { requiresAuth: true, requiresAdmin: true },
  },
  {
    path: "/categories",
    name: "Categories",
    component: CategoryList,
    meta: { requiresAuth: true, requiresAdmin: true },
  },
  {
    path: "/promo-codes",
    name: "PromoCodes",
    component: () => import("@/views/promo-codes/PromoCodeList.vue"),
    meta: { requiresAuth: true, requiresAdmin: true },
  },
  {
    path: "/tutorial-videos",
    name: "TutorialVideos",
    component: () => import("@/views/tutorial-videos/TutorialVideoList.vue"),
    meta: { requiresAuth: true, requiresAdmin: true },
  },
  {
    path: "/tutorial-videos/edit/:id",
    name: "TutorialVideoEdit",
    component: () => import("@/views/tutorial-videos/TutorialVideoEdit.vue"),
    props: true,
    meta: { requiresAuth: true, requiresAdmin: true },
  },
  {
    path: "/master-data/:type",
    name: "MasterData",
    component: MasterData,
    meta: { requiresAuth: true, requiresAdmin: true },
  },

  // ─── Cars (admin + host) ────────────────────────────
  {
    path: "/cars",
    meta: { requiresAuth: true, requiresPanel: true },
    children: [
      { path: "", name: "Cars", component: CarList },
      { path: "create", name: "CarCreate", component: CarCreate },
      { path: "edit/:id", name: "CarEdit", component: CarEdit, props: true },
      { path: "view/:id", name: "CarView", component: CarView, props: true },
      {
        path: ":id/unavailable-periods",
        name: "CarUnavailablePeriods",
        component: () => import("@/views/cars/UnavailablePeriods.vue"),
        props: true,
      },
    ],
  },

  // ─── Admin Bookings ─────────────────────────────────
  {
    path: "/admin",
    meta: { requiresAuth: true, requiresAdmin: true },
    children: [
      {
        path: "bookings",
        name: "AdminBookingsDashboard",
        component: () => import("@/views/admin/AdminBookingsDashboard.vue"),
      },
      {
        path: "notifications",
        name: "Notifications",
        component: () => import("@/views/admin/NotificationsView.vue"),
      },
      {
        path: "whatsapp",
        name: "WhatsAppInbox",
        component: () => import("@/views/admin/WhatsAppInbox.vue"),
      },
      {
        path: "chats",
        name: "Chats",
        component: () => import("@/views/admin/ChatsView.vue"),
        children: [
          {
            path: ":id",
            name: "ChatRoom",
            component: () => import("@/views/admin/ChatRoom.vue"),
            props: true,
          },
        ],
      },
    ],
  },

  // ─── Host Bookings ──────────────────────────────────
  {
    path: "/host",
    meta: { requiresAuth: true, requiresPanel: true },
    children: [
      {
        path: "bookings",
        name: "HostBookings",
        component: () => import("@/views/host/HostBookings.vue"),
      },
    ],
  },

  // ─── Misc ───────────────────────────────────────────
  {
    path: "/about",
    name: "About",
    component: () => import("@/views/AboutView.vue"),
  },
];

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
});

router.beforeEach((to, from, next) => {
  const isAuth = !!localStorage.getItem("admin_token");
  const user = JSON.parse(localStorage.getItem("admin_user") || "{}");
  const roles = user?.roles || [];

  const isAdmin = roles.includes("admin");
  const isHost = roles.includes("host");
  const isModerator = roles.includes("moderator");
  const isOwner =
    roles.includes("individual_owner") || roles.includes("organization_owner");
  const canAccessPanel = isAdmin || isHost || isModerator;

  if (to.meta.requiresAuth && !isAuth) {
    return next({ name: "Login" });
  }

  if (to.meta.requiresAdmin && !isAdmin && !isModerator) {
    return next({ name: "Dashboard" });
  }

  if (to.meta.requiresPanel && !canAccessPanel) {
    return next({ name: "Dashboard" });
  }

  if (to.meta.requiresAnalytics && !isAdmin && !isOwner) {
    return next({ name: "Dashboard" });
  }

  if (to.meta.guestOnly && isAuth) {
    return next({ name: "Dashboard" });
  }

  next();
});

export default router;
